<script setup>
import {computed, ref, watch} from 'vue'
import {useRouter, useRoute} from 'vue-router'
import {useComponentsStore} from '@/stores/components'
import {useLightboxStore} from '@/stores/lightbox'
import {useItemDataStore} from '@/stores/itemData'
import {useViewStore} from '@/stores/view'
import {useAppStore} from "@/stores/appStore"

const props = defineProps({
    viewId: String,
    id: Number,
})

const router = useRouter()
const route = useRoute()

const viewStore = useViewStore(props.viewId)

const appStore = useAppStore()
const componentsStore = useComponentsStore()
const lightboxStore = useLightboxStore()
const itemDataStore = useItemDataStore()

const item = ref(null)
const file = ref(null)

const tableName = computed(() => viewStore.tableName)
const selectedIds = computed(() => viewStore.selectedIds)
const componentId = computed(() => componentsStore.componentIds[tableName.value])
const show = computed(() => item.value && file.value)
const isImage = computed(() => file.value?.mimetype?.indexOf('image') === 0)

const showThumbnail = computed(() => {
    return (
        isImage.value ||
        (item.value && item.value.mimetype === 'application/pdf')
    )
})

const nameField = computed(() => componentsStore.nameField(componentId.value))

// Step 1: Watch props & tableName to fetch `item`
watch(
    () => [props.id, tableName.value],
    async ([newId, newTable]) => {
        if (newId && newTable) {
            item.value = await itemDataStore.get({
                tableName: newTable,
                id: newId
            })
        } else {
            item.value = null
        }
    },
    {immediate: true}
)

// Step 2: Fetch `file` strictly AFTER `item` is available
watch(item, async (newItem) => {
    if (!newItem) {
        file.value = null
        return
    }

    if (tableName.value === 'files') {
        file.value = newItem
        return
    }

    const component = componentsStore.items.find(o => o.id === componentId.value)
    const fileId = component ? newItem[component.thumbnailColumn] : null

    if (fileId) {
        file.value = await itemDataStore.get({
            tableName: 'files',
            id: fileId
        })
    } else {
        file.value = null
    }
}, {immediate: true})

// Step 3: Lightbox registration watcher
watch(file, o => {
    if (o?.fileName && o.mimetype?.indexOf('image') === 0) {
        lightboxStore.add({
            id: item.value.id,
            title: item.value[nameField.value] || '',
            src: appStore.fileSrc(o.fileName),
            onShow: onClick
        })
    }
})

const onClick = () => {
    viewStore.onClick(item.value.id)
}

const onDblClick = async () => {
    try {
        const hasAccess = await viewStore.onDblClick()
        if (hasAccess) {
            router.push({
                name: 'form',
                params: {id: props.id},
                query: route.query
            })
        }
    } catch (error) {
        console.error('Navigation error during double click:', error)
    }
}

const onShiftClick = () => {
    viewStore.onShiftClick(item.value.id)
}

const onMetaClick = () => {
    viewStore.onMetaClick(item.value.id)
}

const showLightbox = async () => {
    if (item.value?.id) {
        // Direct call to Pinia action
        await lightboxStore.show(item.value.id)
    }
}
</script>

<template>
    <div>
        <figure v-if="item && show" :class="{ 'active': selectedIds.indexOf(item.id) !== -1 }" @dblclick="onDblClick"
                @click.left.exact="onClick" @click.meta="onMetaClick" @click.shift="onShiftClick" role="button">

            <Picture v-if="file?.id && showThumbnail" :alt="item[nameField]" :file-id="file.id" :on-click="showLightbox"
                     :sizes="'17vw'"/>
            <div v-else-if="file?.id && !showThumbnail" class="text-center">
                not an image
            </div>
            <div v-else class="text-center">
                no file
            </div>

            <figcaption class="text-center">
                <small>{{ item[nameField] }}</small>
            </figcaption>
        </figure>
    </div>
</template>

<style scoped>
figure {
    display: flex;
    flex-direction: column;
    padding: 0;
    border: none;
    overflow: hidden;
    border-radius: 0;
    height: 100%;
}

figure:hover {
    background-color: #eee;
}

figure.active,
figure.active:hover {
    background-color: #ffcb4a;
}

figure > a {
    flex: auto;
}

figcaption {
    padding-bottom: 3px;
}
</style>